import React from "react";
import { Grid } from "@mui/material";
import Paper from "@mui/material/Paper";
import { Typography, Box } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import {
  useGetFieldAreaListQuery,
  useGetDashboardInstallerQuery,
  useGetTargetInstallsQuery,
  useGetStockOnHandQuery,
} from "../api/apiSlice";
import { FiberManualRecord } from "@mui/icons-material";
import { green } from "@mui/material/colors";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { apexChartDownloadPng } from "../../Global/other/apexChartDownloadPng";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useState, useRef } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function Installs() {
  const [countStockField, setCountStockField] = React.useState(0);

  const { data: areas, isSuccess: isGetAreasSuccess } = useGetFieldAreaListQuery();

  React.useEffect(() => {
    if (isGetAreasSuccess && areas.field_areas.length > 0) {
      const totalStockCount = areas.field_areas.reduce(
        (acc, item) => acc + item.stock_item_count,
        0
      );
      setCountStockField(totalStockCount);
    }
  }, [isGetAreasSuccess, areas, setCountStockField]);

  if (isGetAreasSuccess) {
    return <InstallerCards countStock={countStockField} />;
  }
  return null;
}

function InstallerCards({ countStock }) {
  const chartRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const formattedDate = selectedDate.format("YYYY-MM-DD");

  const { data: installer_details, isSuccess: isInstallerSuccess } =
    useGetDashboardInstallerQuery(formattedDate);
  const { data: target, isSuccess: isTargetSuccess } = useGetTargetInstallsQuery();
  const { data: stock_on_hand, isSuccess: isStockOnHandSuccess } = useGetStockOnHandQuery();

  const renderCircle = (color) => (
    <div
      style={{
        width: "30px",
        height: "4px",
        borderRadius: "20%",
        backgroundColor: color,
      }}
    />
  );

  if (isInstallerSuccess && isTargetSuccess && isStockOnHandSuccess) {
    const installed = installer_details.installed_devices;
    const target_installs = 984;
    const chartSeries = [countStock, target_installs - countStock];
    const percentage = (countStock / target_installs) * 100;

    const handleDownloadPng = () => {
      apexChartDownloadPng(chartRef, `Installs_${selectedDate.format("YYYY-MM-DD")}`);
    };

    const chartOptions = {
      chart: {
        type: "donut",
        background: "transparent",
        foreColor: "#e0e6f0",
        toolbar: { show: false },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "70%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "18px",
                color: "#e0e6f0",
                offsetY: -10,
              },
              value: {
                show: true,
                fontSize: "14px",
                color: "#e0e6f0",
                offsetY: 5,
                formatter: function () {
                  return "Target Installs";
                },
              },
              total: {
                show: true,
                label: `${countStock}/${target_installs}`,
                color: "#e0e6f0",
                fontSize: "24px",
                formatter: function () {
                  return "Target Installs";
                },
              },
            },
          },
        },
      },
      labels: ["Installed", "Target"],
      colors: ["#FDB940", "#2D3748"],
      dataLabels: { enabled: false },
      legend: { show: false },
    };

    return (
      <Box sx={{ pt: "80px", px: 4, pb: 4 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item lg={2} />

          {/* Donut chart card */}
          <Grid item xs={12} lg={2} align="center">
            <Paper
              sx={{
                width: "90%",
                height: 252,
                p: 2,
                backgroundColor: "#161B22",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 4,
              }}
              elevation={0}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: -1, mb: -1 }}>
                <GraphDownloadButton onDownloadPng={handleDownloadPng} />
                <Typography variant="h6" sx={{ color: "#e0e6f0" }}>
                  {percentage.toFixed(2)}%
                </Typography>
              </Box>
              <Chart
                ref={chartRef}
                options={chartOptions}
                series={chartSeries}
                type="donut"
                width="100%"
                height="95%"
              />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: -3 }}>
                <FiberManualRecord style={{ color: green[500] }} />
                <Typography align="center" variant="h6" sx={{ color: "#e0e6f0" }}>
                  {installer_details.connected} Online Devices
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Installs card */}
          <Grid item xs={12} lg={6.5} align="center">
            <Paper
              sx={{
                width: "90%",
                height: 252,
                p: 2,
                backgroundColor: "#161B22",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 4,
              }}
              elevation={0}
            >
              <Grid container sx={{ p: 1 }}>
                <Grid item xs={6} align="left">
                  <Typography sx={{ fontSize: 24, mb: 2, mt: 1, color: "#e0e6f0" }}>
                    Installs
                  </Typography>
                </Grid>
                <Grid item xs={6} align="right">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Date Range"
                      value={selectedDate}
                      onChange={(newDate) => setSelectedDate(newDate)}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            "& .MuiInputBase-root": { color: "#e0e6f0" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
                            "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
                            "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} align="left">
                  <Typography sx={{ display: "inline", fontSize: 32, mr: 1, color: "#e0e6f0" }}>
                    {installed}
                  </Typography>
                  <Typography sx={{ display: "inline", fontSize: 14, color: "rgba(255,255,255,0.5)" }}>
                    Installed today
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <LinearProgress
                    variant="determinate"
                    value={(installed / (stock_on_hand.count + installed)) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mt: 2,
                      backgroundColor: "#2D3748",
                      "& .MuiLinearProgress-bar": { backgroundColor: "#FDB940" },
                    }}
                  />
                </Grid>

                <Grid item xs={1.5} md={1} sx={{ mt: 5 }} align="left">
                  {renderCircle("#FDB940")}
                </Grid>
                <Grid item xs={4} sx={{ mt: 4 }} align="left">
                  <Typography sx={{ display: "inline", fontSize: 16, color: "rgba(255,255,255,0.5)" }}>
                    Devices installed
                  </Typography>
                </Grid>
                <Grid item xs={0.5} md={0} sx={{ mt: 4 }} align="left" />
                <Grid item xs={1.5} md={1} sx={{ mt: 5 }} align="left">
                  {renderCircle("#2D3748")}
                </Grid>
                <Grid item xs={4} sx={{ mt: 4 }} align="left">
                  <Typography sx={{ display: "inline", fontSize: 16, color: "rgba(255,255,255,0.5)" }}>
                    Dispatched Stock
                  </Typography>
                </Grid>
                <Grid item xs={1} md={1.5} />
                <Grid item md={0.5} />
                <Grid item xs={1.5} md={1} align="right">
                  <Typography sx={{ display: "inline", fontSize: 16, color: "rgba(255,255,255,0.75)" }}>
                    {installed}
                  </Typography>
                </Grid>
                <Grid item xs={5} md={5} align="left" />
                <Grid item xs={1.5} md={1} align="left">
                  <Typography sx={{ display: "inline", fontSize: 16, color: "rgba(255,255,255,0.75)" }}>
                    {stock_on_hand.count}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item lg={1.5} />
        </Grid>
      </Box>
    );
  }

  return null;
}
